/**
 * THE CONTROL LAW — the few class strings every primitive obeys, written once.
 *
 * WHY THIS FILE EXISTS. Thirty-six primitives each carried their own spelling
 * of "focused", "disabled", "sits on a surface". The spellings drifted, and
 * each drift was a slightly different outline on screen. The rules below are
 * the only spellings.
 *
 * THE REGISTER (2026-09-12, graphite). Depth comes from a border, not a
 * shadow: a card is `bg-card` plus `border-border`, and hovering a clickable
 * card firms the border to `border-border-strong`. Nothing in content lifts,
 * blooms or carries a sheen; only floating surfaces (rungs 3 and 4) cast a
 * shadow. Focus is a 2px accent ring offset from the control. Selection —
 * an open select, the highlighted palette row — is the same ring as a
 * box-shadow (`shadow-ring`) so it composes with the control's own border.
 *
 * The previous law said the opposite ("depth is the default, flatness is the
 * exception"): every filled control carried a top-edge sheen, a hover lift of
 * one pixel plus a bigger shadow, and the primary action wore a glow. It was
 * removed with the palette change because under a black/white register a
 * highlight on a black button reads as lit plastic and a lifted grey card
 * reads as a widget. Motion stays: `transform` remains in the interactive
 * transition so a pressed control can still respond.
 */

/* ── FOCUS ──────────────────────────────────────────────────────────────── */

/** Focus: accent ring at the density width, offset from the control. */
export const FOCUS =
  "outline-none focus-visible:ring-(length:--ring-w) focus-visible:ring-offset-(length:--ring-offset) focus-visible:ring-offset-background focus-visible:ring-ring";

/** Same, for elements whose focus arrives as `:focus` (Radix close buttons). */
export const FOCUS_PLAIN =
  "outline-none focus:ring-(length:--ring-w) focus:ring-offset-(length:--ring-offset) focus:ring-offset-background focus:ring-ring";

/** The selected surface's outline: the ring as a box-shadow. */
export const SELECTED_RING = "shadow-ring";

/* ── STATE ──────────────────────────────────────────────────────────────── */

/** Invalid: the danger hue on border and ring. */
export const INVALID = "aria-invalid:border-danger aria-invalid:ring-danger";

/** Disabled: no pointer, half opacity, no shadow — a dead control must not respond. */
export const DISABLED =
  "disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none";

/* ── MOTION ─────────────────────────────────────────────────────────────── */

/**
 * Colour and outline, but no movement — for recessed things (inputs) and
 * things that must not travel (badges, static rows).
 *
 * `box-shadow` stays in the list: the selected ring IS a box-shadow, so
 * dropping it makes the ring snap on instead of easing.
 */
export const TRANSITION_COLOR =
  "transition-[color,background-color,border-color,box-shadow,opacity] duration-(--dur-fast) ease-(--ease-out)";

/**
 * The interactive constant. Colour AND outline AND transform, so a pressed
 * control can compress even though nothing lifts on hover.
 */
export const TRANSITION_INTERACTIVE =
  "transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-(--dur-fast) ease-(--ease-out)";

/** Something travelling — panels, sheets, popovers. Springs rather than eases. */
export const TRANSITION_MOVE =
  "transition-[transform,opacity] duration-(--dur-base) ease-(--ease-spring)";

/* ── SURFACES ───────────────────────────────────────────────────────────── */

/** A control resting on a surface: bordered, rung one. */
export const SURFACE_CONTROL = "border border-border bg-background shadow-1";

/** A card: bordered, flat. Rung two is `none` on purpose. */
export const SURFACE_CARD = "border border-border bg-card text-card-foreground";

/** A floating overlay (popover, menu, tooltip): rung three. */
export const SURFACE_OVERLAY =
  "border border-border bg-popover text-popover-foreground shadow-3";

/** A modal: rung four. */
export const SURFACE_MODAL =
  "border border-border bg-popover text-popover-foreground shadow-4";

/**
 * The hover affordance for any surface the user can act on: the border
 * firms. No travel, no shadow — a grid of forty cards must not shimmer.
 */
export const LIFT = "hover:border-border-strong";

/** A row in a list: no border of its own, lit on hover, marked when selected. */
export const ROW =
  "rounded-md hover:bg-accent hover:text-accent-foreground data-[state=selected]:bg-selection";

/* ── COMPOSED ───────────────────────────────────────────────────────────── */

/** Everything an interactive control needs except its own colours and size. */
export const CONTROL = [FOCUS, DISABLED, INVALID, TRANSITION_INTERACTIVE].join(" ");

/** An interactive card: the card surface, plus the hover border, plus focus. */
export const CARD_INTERACTIVE = [
  SURFACE_CARD,
  LIFT,
  FOCUS,
  TRANSITION_INTERACTIVE,
].join(" ");

/** Icon sizing inside a control: the shadcn selector, unchanged. */
export const CONTROL_ICON =
  "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

/** Entrance for a row or panel arriving in a list. */
export const ENTER = "animate-rise-in";
